import React, { useEffect, useRef, useState } from 'react';
import { Message, User, Attachment } from '../../types';
import { chatService } from '../../services/chatService';
import { MessageBubble } from './MessageBubble';
import { ChatInput } from './ChatInput';
import { Sidebar } from './Sidebar';
import { Menu, X } from 'lucide-react';
import { APP_VERSION } from '../../version';

interface ChatWindowProps {
  currentUser: User;
}



export const ChatWindow: React.FC<ChatWindowProps> = ({ currentUser }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [customStatusText, setCustomStatusText] = useState(currentUser.customStatus);
  const [isScrolledUp, setIsScrolledUp] = useState(false);
  const [enlargedImage, setEnlargedImage] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const hasInitialScrolled = useRef(false);

  useEffect(() => {
    const unsubMsg = chatService.subscribeToMessages(setMessages);
    const unsubUsers = chatService.subscribeToUsers((users) => {
      setUsers(users);
      const me = users.find(u => u.id === currentUser.id);
      if (me) {
        setCustomStatusText(me.customStatus);
      }
    });
    return () => { unsubMsg(); unsubUsers(); };
  }, [currentUser.id]);

  useEffect(() => {
    if (messages.length === 0) return;

    if (!hasInitialScrolled.current) {
      const lastOwnMessage = [...messages].reverse().find(m => m.senderId === currentUser.id);

      if (lastOwnMessage) {
        const el = document.getElementById(`message-${lastOwnMessage.id}`);
        if (el) {
          hasInitialScrolled.current = true;
          el.scrollIntoView({ behavior: 'instant', block: 'end' });
          return;
        }
        // Element not in DOM yet — wait for next render
        return;
      }

      // No own messages: scroll to end
      hasInitialScrolled.current = true;
      messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
      return;
    }

    if (!isScrolledUp) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isScrolledUp, currentUser.id]);

  const handleSendMessage = async (content: string, attachments: Attachment[]) => {
    await chatService.sendMessage(currentUser.id, content, attachments);
  };

  const getUserById = (id: string) => users.find(u => u.id === id);

  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (container) {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const atBottom = scrollHeight - scrollTop <= clientHeight + 1;
      setIsScrolledUp(!atBottom);
    }
  };

  return (
    <div className="flex h-full bg-white overflow-hidden">
      {isSidebarOpen && <div className="fixed inset-0 bg-black/50 z-20 md:hidden" onClick={() => setIsSidebarOpen(false)} />}
      <div className={`fixed inset-y-0 left-0 z-30 w-80 transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <Sidebar users={users} currentUser={currentUser} customStatusText={customStatusText} setCustomStatusText={setCustomStatusText} onClose={() => setIsSidebarOpen(false)} />
      </div>
      <div className="flex-1 flex flex-col min-w-0 bg-gray-50 overflow-x-hidden">
        <div className="md:hidden sticky top-0 z-10 flex items-center p-4 bg-white border-b border-gray-200 shadow-sm">
          <button onClick={() => setIsSidebarOpen(true)} className="p-2 -ml-2 mr-2 text-gray-600 hover:bg-gray-100 rounded-lg"><Menu className="w-6 h-6" /></button>
          <h1 className="font-bold text-lg text-gray-800">Exclusive messaging {APP_VERSION}</h1>
        </div>
        <div ref={messagesContainerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6 space-y-2">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-gray-400 space-y-4">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center"><span className="text-4xl">👋</span></div>
              <p className="text-lg font-medium">Welcome to Exclusive messaging!</p>
            </div>
          ) : (
            messages.map((msg) => (
              <div key={msg.id} id={`message-${msg.id}`}>
                <MessageBubble message={msg} isOwn={msg.senderId === currentUser.id} sender={getUserById(msg.senderId)} users={users} onImageClick={setEnlargedImage} />
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>
        <ChatInput onSendMessage={handleSendMessage} />
      </div>
      {enlargedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setEnlargedImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2 hover:bg-black/70 transition-colors"
            onClick={() => setEnlargedImage(null)}
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={enlargedImage}
            alt="enlarged"
            className="max-w-full max-h-full object-contain rounded-lg cursor-zoom-out"
            onClick={() => setEnlargedImage(null)}
          />
        </div>
      )}
    </div>
  );
};
